const SEARCH_SETTLE_MS = 1_000;
const SEARCH_REPEAT_WINDOW_MS = 30_000;
const MAX_RECENT_QUERIES = 100;

export function normalizeSearchQuery(query: string) {
	return query.trim().replace(/\s+/g, ' ').toLowerCase();
}

type SearchTrackingClock = {
	now: () => number;
	schedule: (callback: () => void, delay: number) => () => void;
};

const browserClock: SearchTrackingClock = {
	now: () => Date.now(),
	schedule(callback, delay) {
		const timer = setTimeout(callback, delay);
		return () => clearTimeout(timer);
	},
};

/** Coalesce live search updates without delaying the search results themselves. */
export function createSearchEventTracker(clock: SearchTrackingClock = browserClock) {
	let inputQuery: string | null = null;
	let pending: { key: string; query: string; send: () => void } | null = null;
	let cancelTimer: (() => void) | undefined;
	const recentlySent = new Map<string, number>();

	function stopTimer() {
		cancelTimer?.();
		cancelTimer = undefined;
	}

	function rememberSent(key: string) {
		const now = clock.now();
		for (const [recentKey, at] of recentlySent) {
			if (now - at >= SEARCH_REPEAT_WINDOW_MS) recentlySent.delete(recentKey);
		}
		if (recentlySent.size >= MAX_RECENT_QUERIES) {
			const oldest = recentlySent.keys().next().value;
			if (oldest !== undefined) recentlySent.delete(oldest);
		}
		recentlySent.set(key, now);
	}

	function arm() {
		stopTimer();
		const candidate = pending;
		if (!candidate || (inputQuery !== null && candidate.query !== inputQuery)) return;
		const sentAt = recentlySent.get(candidate.key);
		if (sentAt !== undefined && clock.now() - sentAt < SEARCH_REPEAT_WINDOW_MS) return;

		cancelTimer = clock.schedule(() => {
			cancelTimer = undefined;
			if (pending !== candidate) return;
			pending = null;
			rememberSent(candidate.key);
			candidate.send();
		}, SEARCH_SETTLE_MS);
	}

	return {
		// Called on input, before a slow router/server response can finish. Keep
		// the candidate so input/result effects can arrive in either order.
		setInputQuery(query: string | null) {
			const normalized = query === null ? null : normalizeSearchQuery(query);
			if (normalized === inputQuery) return;
			inputQuery = normalized;
			arm();
		},
		schedule(pathname: string, query: string, send: () => void) {
			const normalized = normalizeSearchQuery(query);
			const candidate = normalized
				? { key: JSON.stringify([pathname, normalized]), query: normalized, send }
				: null;
			pending = candidate;
			arm();
			return () => {
				// An old effect cleanup must not cancel a newer result's timer.
				if (pending !== candidate) return;
				pending = null;
				stopTimer();
			};
		},
	};
}

// Memory only: survives quick component remounts, without storing searches in
// cookies/localStorage. Mutated only by client effects and input handlers.
export const searchEventTracker = createSearchEventTracker();
